import {createSelector} from '@reduxjs/toolkit'

import {eq} from '@shared/helpers/lang'

import {CPID, PayMethod, ReceiptAdditionalPolicy, SubscribePayMethodType} from '@web/core/constants'
import {cashReceiptSelector} from '@web/slices/cashReceipt/slice'
import {pageSetInfoSelector} from '@web/slices/common/slice'
import {merchantCpidSelector} from '@web/slices/merchants/slice'
import {naverPayPointSelector} from '@web/slices/pointPayment/slice'
import {
    selectedEasyCardSelector,
    selectedMethodSelector,
    selectedSecondEasyCardSelector,
    selectedSecondPayMethodSelector,
} from '@web/slices/priorityPaymentMethod/slice'

/** 현금영수증 발급 가능 결제수단 목록 */
const getPossibleMethods = ({
    isPaymentMethodManagement,
    isDiconAuto,
}: {
    isPaymentMethodManagement: boolean
    isDiconAuto: boolean
}): SubscribePayMethodType[] => {
    if (isPaymentMethodManagement) {
        return [PayMethod.RECHARGE_POINT]
    } else if (isDiconAuto) {
        return [PayMethod.BANK_EASY_PAY, PayMethod.RECHARGE_POINT]
    }
    return []
}

/**
 * 현금영수증 발행 가능 여부
 * 현금성포인트 정책으로 인해 페이머니 여부와 관계 없이
 * 포인트 사용 시 현금영수증 발행 필요
 **/
export const canHaveCashReceiptSelector = createSelector(
    pageSetInfoSelector,
    merchantCpidSelector,
    cashReceiptSelector,
    selectedMethodSelector,
    selectedSecondPayMethodSelector,
    selectedEasyCardSelector,
    selectedSecondEasyCardSelector,
    naverPayPointSelector,
    (
        {isDiconAuto, isPaymentMethodManagement},
        merchantCpid,
        {available},
        selectedMethod,
        selectedSecondPayMethod,
        selectedEasyCard,
        selectedSecondEasyCard,
        {usePointAll},
    ) => {
        const possibleMethods = getPossibleMethods({isPaymentMethodManagement, isDiconAuto})
        const isHappyBean = merchantCpid === CPID.HAPPYBEAN
        /**
         * 1순위 결제수단에서
         * 카드간편결제 & 네이버페이머니카드 선택여부
         **/
        const isPaymoneyCardSelected =
            eq(selectedMethod, PayMethod.CARD_EASY_PAY) &&
            eq(selectedEasyCard?.cardType, ReceiptAdditionalPolicy.NFC_PREPAID)
        /**
         * 2순위 결제수단에서
         * 카드간편결제 & 네이버페이머니카드 선택여부
         */
        const isPaymoneyCardSelectedSecond =
            eq(selectedSecondPayMethod, PayMethod.CARD_EASY_PAY) &&
            eq(selectedSecondEasyCard?.cardType, ReceiptAdditionalPolicy.NFC_PREPAID)

        return (
            available &&
            !isHappyBean &&
            (possibleMethods.includes(selectedMethod) ||
                possibleMethods.includes(selectedSecondPayMethod) ||
                usePointAll ||
                isPaymoneyCardSelected ||
                isPaymoneyCardSelectedSecond)
        )
    },
)
